import React, { useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import ResponsiveModal from '../layout/ResponsiveModal';

const ModalDenuncia = ({ isOpen, onClose }) => {
  const [anonima, setAnonima] = useState('si');
  const [mostrarPolitica, setMostrarPolitica] = useState(false);
  const [loading, setLoading] = useState(false);

  // Form state
  const [nombre, setNombre] = useState('');
  const [apellidos, setApellidos] = useState('');
  const [correo, setCorreo] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [testigos, setTestigos] = useState('');
  const [acciones, setAcciones] = useState('');
  const [archivos, setArchivos] = useState(null);
  const [aceptaPolitica, setAceptaPolitica] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!aceptaPolitica) {
      Swal.fire({
        title: 'Atención',
        text: 'Debes aceptar la Política de Privacidad del Canal de Denuncias para continuar.',
        icon: 'warning',
        confirmButtonColor: '#C8102E'
      });
      return;
    }

    const formData = new FormData();
    formData.append('anonima', anonima);
    if (anonima === 'no') {
      formData.append('nombre', nombre);
      formData.append('apellidos', apellidos);
      formData.append('correo', correo);
    }
    formData.append('descripcion', descripcion);
    formData.append('testigos', testigos);
    formData.append('acciones_tomadas', acciones);
    formData.append('acepta_politica', aceptaPolitica ? 'true' : 'false');

    if (archivos) {
      for (let i = 0; i < archivos.length; i++) {
        formData.append('archivosDenuncia', archivos[i]);
      }
    }

    setLoading(true);

    try {
      const url = 'https://www.digitalfactura.com/sicaiwebsys/SicaiWeb/api/Public/CanalDenuncias';
      const response = await axios.post(url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.status === 200 || response.status === 201) {
        Swal.fire({
          title: '¡Denuncia Recibida!',
          text: 'La denuncia ha sido transmitida de forma segura y confidencial. Se abrirá la investigación conforme al protocolo legal.',
          icon: 'success',
          confirmButtonColor: '#C8102E'
        }).then(() => {
          onClose();
          // Reset form
          setAnonima('si');
          setNombre('');
          setApellidos('');
          setCorreo('');
          setDescripcion('');
          setTestigos('');
          setAcciones('');
          setArchivos(null);
          setAceptaPolitica(false);
          setMostrarPolitica(false);
        });
      } else {
        Swal.fire({
          title: 'Error',
          text: response.data.message || 'Hubo un problema al procesar la denuncia.',
          icon: 'error',
          confirmButtonColor: '#C8102E'
        });
      }
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Ocurrió un error de conexión con el servidor.';
      Swal.fire({
        title: 'Error',
        text: errorMsg,
        icon: 'error',
        confirmButtonColor: '#C8102E'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={onClose}
      title="Canal Ético y de Denuncias"
      subtitle="Reporta conductas no éticas, sospechas de corrupción, sobornos o irregularidades de manera confidencial."
      size="lg"
      footer={
        <>
          <button type="button" className="df-btn df-btn-outline" onClick={onClose} disabled={loading}>
            Cancelar
          </button>
          <button type="submit" form="form-denuncia-modal" className="df-btn df-btn-primary" disabled={loading}>
            {loading ? 'Transmitiendo...' : 'Enviar Denuncia Segura'}
          </button>
        </>
      }
    >
      <form id="form-denuncia-modal" onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {/* Opción Anónima */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <label className="df-form-label">¿Deseas que tu reporte sea anónimo?</label>
          <div style={{ display: 'flex', gap: '20px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '0.95rem' }}>
              <input
                type="radio"
                name="anonima"
                value="si"
                checked={anonima === 'si'}
                onChange={() => setAnonima('si')}
                style={{ accentColor: 'var(--df-primary)' }}
              />
              <strong>Sí</strong>, mantener mi identidad en anonimato
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '0.95rem' }}>
              <input
                type="radio"
                name="anonima"
                value="no"
                checked={anonima === 'no'}
                onChange={() => setAnonima('no')}
                style={{ accentColor: 'var(--df-primary)' }}
              />
              <strong>No</strong>, deseo identificarme
            </label>
          </div>
        </div>

        {/* Campos de Identificación si no es anónima */}
        {anonima === 'no' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label className="df-form-label" htmlFor="den-nombre">Nombre *</label>
              <input
                id="den-nombre"
                type="text"
                className="df-form-input"
                required
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label className="df-form-label" htmlFor="den-apellidos">Apellidos *</label>
              <input
                id="den-apellidos"
                type="text"
                className="df-form-input"
                required
                value={apellidos}
                onChange={(e) => setApellidos(e.target.value)}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label className="df-form-label" htmlFor="den-correo">Correo electrónico *</label>
              <input
                id="den-correo"
                type="email"
                className="df-form-input"
                required
                value={correo}
                onChange={(e) => setCorreo(e.target.value)}
              />
            </div>
          </div>
        )}

        {/* Descripción de los Hechos */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label className="df-form-label" htmlFor="den-desc">Descripción de los hechos *</label>
          <small style={{ color: 'var(--df-text-muted)' }}>Fecha, personas involucradas, cargo o relación con la empresa y relato detallado.</small>
          <textarea
            id="den-desc"
            className="df-form-textarea"
            rows="4"
            required
            placeholder="Relata lo sucedido con el mayor detalle posible..."
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
          />
        </div>

        {/* Testigos */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label className="df-form-label" htmlFor="den-testigos">Testigos (opcional)</label>
          <small style={{ color: 'var(--df-text-muted)' }}>Nombres y datos de contacto de personas que presenciaron los hechos.</small>
          <textarea
            id="den-testigos"
            className="df-form-textarea"
            rows="2"
            value={testigos}
            onChange={(e) => setTestigos(e.target.value)}
          />
        </div>

        {/* Acciones Tomadas */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label className="df-form-label" htmlFor="den-acciones">Acciones previas tomadas (opcional)</label>
          <small style={{ color: 'var(--df-text-muted)' }}>¿Has informado esto a alguien más dentro de la organización? ¿A quién?</small>
          <textarea
            id="den-acciones"
            className="df-form-textarea"
            rows="2"
            value={acciones}
            onChange={(e) => setAcciones(e.target.value)}
          />
        </div>

        {/* Adjuntar Archivos */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label className="df-form-label" htmlFor="den-archivos">Adjuntar evidencias o documentos probatorios</label>
          <small style={{ color: 'var(--df-text-muted)' }}>Fotografías, correos, capturas de pantalla, archivos de audio o PDF.</small>
          <input
            id="den-archivos"
            type="file"
            multiple
            className="df-form-input"
            onChange={(e) => setArchivos(e.target.files)}
          />
          {anonima === 'si' && (
            <div style={{ background: 'var(--df-warning-bg)', border: '1px solid #FFD8A8', padding: '8px 12px', borderRadius: '6px', fontSize: '0.82rem', color: '#974C00', marginTop: '4px' }}>
              ⚠️ <strong>Atención:</strong> Si la denuncia es anónima, asegúrate de que los archivos adjuntos no contengan metadatos, nombres de archivo o membretes que revelen tu identidad.
            </div>
          )}
        </div>

        {/* Checkbox Política de Privacidad */}
        <div>
          <label className="df-form-checkbox-label">
            <input
              type="checkbox"
              className="df-form-checkbox"
              required
              checked={aceptaPolitica}
              onChange={(e) => setAceptaPolitica(e.target.checked)}
            />
            <span>
              He leído y estoy conforme con la{' '}
              <button
                type="button"
                className="df-form-link-btn"
                onClick={(e) => {
                  e.preventDefault();
                  setMostrarPolitica(!mostrarPolitica);
                }}
              >
                {mostrarPolitica ? 'Ocultar política' : 'Política de Privacidad del Canal de Denuncias'}
              </button>
            </span>
          </label>

          {mostrarPolitica && (
            <div style={{ marginTop: '10px', padding: '14px', background: 'var(--df-bg-alt)', borderRadius: '8px', border: '1px solid var(--df-border)', fontSize: '0.85rem', maxHeight: '180px', overflowY: 'auto' }}>
              <p><strong>Responsable del Tratamiento:</strong> Digital Factura (marca de Impresos de Caber, S.A. de C.V.).</p>
              <p><strong>Finalidad:</strong> Tramitar e investigar hechos comunicados a través del canal ético y anticorrupción con estricta confidencialidad y garantía de no represalias.</p>
              <p><strong>Derechos ARCO:</strong> Puedes ejercer tus derechos de acceso, rectificación, cancelación u oposición mediante los canales oficiales.</p>
            </div>
          )}
        </div>
      </form>
    </ResponsiveModal>
  );
};

export default ModalDenuncia;
